/**
 * Parties tab — victim(s) and the defendant, case-file records.
 *
 * Two sub-tabs (Defendant / Victim). A case may have SEVERAL victims (full
 * add/edit/delete list) but only ONE defendant (a single slot — "Add" becomes
 * unavailable once one is recorded; edit/delete act on that one record).
 * Scoring-relevant fields (age class, type, LE status, relationship,
 * conduct-contested, defendant status) feed the SpatchVenire victim/defendant
 * modifiers via aggregateParties() in core/scoring-sv.ts — a victim modifier
 * fires if it applies to ANY recorded victim (e.g. one child victim among
 * several still raises parent-empathy signals); the defendant modifiers are
 * plain single-record checks.
 *
 * Case-fact fields (race/ethnicity, gender, religion, appearance,
 * socioeconomic status) are descriptive notes ONLY — never wired into juror
 * scoring or juror-demographic matching. Per the voir-dire guardrails, the
 * app already asks uniform, panel-wide party-characteristic impartiality
 * questions of every juror regardless of what is recorded here.
 */

import type {
  VictimRecord,
  DefendantRecord,
  VictimAgeClass,
  VictimType,
  VictimRelationship,
  DefendantStatus
} from '@/types';
import { store } from '@/app/store';
import { showModal, closeModal, confirmApp, showToast } from '@/ui/overlays';
import { escapeHTML } from '@/utils/formatters';

type Role = 'victim' | 'defendant';
let activeTab: Role = 'defendant';

function uid(): string {
  return 'p' + Math.random().toString(36).slice(2, 10);
}

// ---------- page shell ----------

export function showPartiesSubTab(tab: Role): void {
  activeTab = tab;
  document.getElementById('ptab-defendant')?.classList.toggle('active', tab === 'defendant');
  document.getElementById('ptab-victim')?.classList.toggle('active', tab === 'victim');
  renderPartiesPage();
}

export function renderPartiesPage(): void {
  const host = document.getElementById('parties-content');
  if (!host) return;
  host.innerHTML = activeTab === 'defendant' ? defendantSectionHtml() : victimListHtml();
}

// ---------- Defendant (single record) ----------

function defendantSectionHtml(): string {
  const d = store.getState().defendant;
  const body = d
    ? defendantCardHtml(d)
    : '<div style="font-size:12px;color:var(--text-muted);padding:14px 4px;">No defendant recorded yet.</div>' +
      '<button class="btn btn-primary btn-sm" style="width:100%;" onclick="openPartyForm(\'defendant\')">+ Add Defendant</button>';
  return (
    '<div class="card">' +
    '<div class="card-title"><div class="cticon"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" stroke-width="2.5"><circle cx="12" cy="8" r="4"/><path d="M6 21v-2a6 6 0 0 1 12 0v2"/></svg></div>Defendant</div>' +
    '<p style="font-size:11px;color:var(--text-secondary);margin-bottom:9px;">Case-file facts about the defendant (a case has one). <strong>Status</strong> feeds the SpatchVenire evaluation — e.g. an officer-defendant inverts the authoritarianism signal. Other fields are case notes only; they are never used to score or match jurors.</p>' +
    body +
    '</div>'
  );
}

function defendantCardHtml(d: DefendantRecord): string {
  const chips = [d.status && statusLabel(d.status)].filter(Boolean) as string[];
  return (
    '<div style="border:1px solid #e2e8f0;border-radius:10px;padding:10px 12px;">' +
    '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">' +
    `<div><div style="font-size:12px;font-weight:700;color:#334155;">${escapeHTML(d.label || 'Defendant')}</div>` +
    (chips.length
      ? `<div style="margin-top:3px;">${chips
          .map((c) => `<span style="display:inline-block;background:#eff6ff;color:#1e40af;font-size:10px;font-weight:600;border-radius:5px;padding:1px 7px;margin-right:5px;">${escapeHTML(c)}</span>`)
          .join('')}</div>`
      : '') +
    (d.notes ? `<div style="font-size:11px;color:var(--text-secondary);margin-top:5px;">${escapeHTML(d.notes)}</div>` : '') +
    '</div>' +
    '<div style="display:flex;gap:6px;flex-shrink:0;">' +
    '<button class="btn btn-secondary btn-sm" style="padding:4px 9px;font-size:11px;" onclick="openPartyForm(\'defendant\')">Edit</button>' +
    '<button class="btn btn-sm" style="padding:4px 9px;font-size:11px;background:#dc2626;color:#fff;border:none;" onclick="deleteParty(\'defendant\')">Delete</button>' +
    '</div></div></div>'
  );
}

function statusLabel(s: DefendantStatus): string {
  return { individual: 'Individual', corporate: 'Corporate', officer: 'Police / Officer', juvenile: 'Juvenile', '': '' }[s] || '';
}

// ---------- Victim list (multiple) ----------

function victimListHtml(): string {
  const list = store.getState().victims;
  const cards = list.length
    ? list.map(victimCardHtml).join('')
    : '<div style="font-size:12px;color:var(--text-muted);padding:14px 4px;">No victims recorded yet.</div>';
  return (
    '<div class="card">' +
    '<div class="card-title"><div class="cticon"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>Victims</div>' +
    '<p style="font-size:11px;color:var(--text-secondary);margin-bottom:9px;">Case-file facts about each alleged victim (a case may have several). <strong>Age class, type, LE status, relationship, and contested conduct</strong> feed the SpatchVenire evaluation. Other fields are case notes only; they are never used to score or match jurors.</p>' +
    '<button class="btn btn-primary btn-sm" style="width:100%;margin-bottom:10px;" onclick="openPartyForm(\'victim\')">+ Add Victim</button>' +
    cards +
    '</div>'
  );
}

function victimCardHtml(v: VictimRecord): string {
  const chips = [
    v.ageClass && ageClassLabel(v.ageClass),
    v.type && typeLabel(v.type),
    v.isLE ? 'LE / first responder' : '',
    v.conductContested ? 'Conduct contested' : ''
  ].filter(Boolean) as string[];
  return (
    '<div style="border:1px solid #e2e8f0;border-radius:10px;padding:10px 12px;margin-bottom:8px;">' +
    '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">' +
    `<div><div style="font-size:12px;font-weight:700;color:#334155;">${escapeHTML(v.label || 'Victim')}</div>` +
    (chips.length
      ? `<div style="margin-top:3px;">${chips
          .map((c) => `<span style="display:inline-block;background:#f0fdf4;color:#15803d;font-size:10px;font-weight:600;border-radius:5px;padding:1px 7px;margin-right:5px;">${escapeHTML(c)}</span>`)
          .join('')}</div>`
      : '') +
    (v.notes ? `<div style="font-size:11px;color:var(--text-secondary);margin-top:5px;">${escapeHTML(v.notes)}</div>` : '') +
    '</div>' +
    '<div style="display:flex;gap:6px;flex-shrink:0;">' +
    `<button class="btn btn-secondary btn-sm" style="padding:4px 9px;font-size:11px;" onclick="openPartyForm('victim','${v.id}')">Edit</button>` +
    `<button class="btn btn-sm" style="padding:4px 9px;font-size:11px;background:#dc2626;color:#fff;border:none;" onclick="deleteParty('victim','${v.id}')">Delete</button>` +
    '</div></div></div>'
  );
}

function ageClassLabel(a: VictimAgeClass): string {
  return { child: 'Child victim', adult: 'Adult victim', elder: 'Elder victim', '': '' }[a] || '';
}
function typeLabel(t: VictimType): string {
  return { individual: 'Individual', institutional: 'Institutional', '': '' }[t] || '';
}

// ---------- Add/Edit form (modal) ----------

function opt(value: string, label: string, current: string): string {
  return `<option value="${value}"${value === current ? ' selected' : ''}>${label}</option>`;
}

/** id is only meaningful for role 'victim' — the defendant is a single slot. */
export function openPartyForm(role: Role, id?: string): void {
  const isVictim = role === 'victim';
  const existing: VictimRecord | DefendantRecord | undefined = isVictim
    ? (id ? store.getState().victims.find((x) => x.id === id) : undefined)
    : store.getState().defendant || undefined;

  const label = existing?.label || '';
  const notes = existing?.notes || '';
  const raceEthnicity = existing?.raceEthnicity || '';
  const gender = existing?.gender || '';
  const religion = existing?.religion || '';
  const appearanceNotes = existing?.appearanceNotes || '';
  const ses = existing?.socioeconomicStatus || '';

  let scoringFields: string;
  if (isVictim) {
    const v = existing as VictimRecord | undefined;
    scoringFields =
      '<div class="form-row">' +
      `<div class="fg"><label>Age class</label><select id="pf-ageClass">${opt('', '—', v?.ageClass || '')}${opt('child', 'Child', v?.ageClass || '')}${opt('adult', 'Adult', v?.ageClass || '')}${opt('elder', 'Elder', v?.ageClass || '')}</select></div>` +
      `<div class="fg"><label>Type</label><select id="pf-type">${opt('', '—', v?.type || '')}${opt('individual', 'Individual', v?.type || '')}${opt('institutional', 'Institutional', v?.type || '')}</select></div>` +
      '</div>' +
      `<div class="fg"><label>Relationship to defendant</label><select id="pf-relationship">${opt('', '—', v?.relationshipToDefendant || '')}${opt('stranger', 'Stranger', v?.relationshipToDefendant || '')}${opt('acquaintance', 'Acquaintance', v?.relationshipToDefendant || '')}${opt('intimate-family', 'Intimate / family', v?.relationshipToDefendant || '')}${opt('business', 'Business', v?.relationshipToDefendant || '')}</select></div>` +
      `<label style="display:flex;align-items:center;gap:8px;font-size:12px;margin-bottom:6px;"><input type="checkbox" id="pf-isLE" ${v?.isLE ? 'checked' : ''} style="width:15px;height:15px;"> Victim is police / first responder</label>` +
      `<label style="display:flex;align-items:center;gap:8px;font-size:12px;margin-bottom:6px;"><input type="checkbox" id="pf-conductContested" ${v?.conductContested ? 'checked' : ''} style="width:15px;height:15px;"> Victim's conduct or credibility is contested</label>`;
  } else {
    const d = existing as DefendantRecord | undefined;
    scoringFields = `<div class="fg"><label>Status</label><select id="pf-status">${opt('', '—', d?.status || '')}${opt('individual', 'Individual', d?.status || '')}${opt('corporate', 'Corporate', d?.status || '')}${opt('officer', 'Police / Officer', d?.status || '')}${opt('juvenile', 'Juvenile (adult court)', d?.status || '')}</select></div>`;
  }

  const html =
    `<div class="modal-title" style="margin-bottom:10px;">${existing ? 'Edit' : 'Add'} ${isVictim ? 'Victim' : 'Defendant'}</div>` +
    `<div class="fg"><label>Label / name (optional)</label><input id="pf-label" type="text" value="${escapeHTML(label)}" placeholder="${isVictim ? 'Victim' : 'Defendant'}"></div>` +
    scoringFields +
    '<div style="margin-top:6px;padding-top:8px;border-top:1px dashed #e2e8f0;">' +
    '<div style="font-size:10px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:.4px;margin-bottom:6px;">Case notes (never used to score or match jurors)</div>' +
    '<div class="form-row">' +
    `<div class="fg"><label>Race / ethnicity</label><input id="pf-raceEthnicity" type="text" value="${escapeHTML(raceEthnicity)}"></div>` +
    `<div class="fg"><label>Gender</label><input id="pf-gender" type="text" value="${escapeHTML(gender)}"></div>` +
    '</div>' +
    '<div class="form-row">' +
    `<div class="fg"><label>Religion</label><input id="pf-religion" type="text" value="${escapeHTML(religion)}"></div>` +
    `<div class="fg"><label>Socioeconomic status</label><input id="pf-ses" type="text" value="${escapeHTML(ses)}"></div>` +
    '</div>' +
    `<div class="fg"><label>Appearance / notable characteristics</label><textarea id="pf-appearance" rows="2">${escapeHTML(appearanceNotes)}</textarea></div>` +
    `<div class="fg"><label>Notes</label><textarea id="pf-notes" rows="2">${escapeHTML(notes)}</textarea></div>` +
    '</div>' +
    '<div style="display:flex;gap:8px;margin-top:12px;">' +
    '<button class="btn btn-secondary" style="flex:1;" onclick="closeModal()">Cancel</button>' +
    `<button class="btn btn-primary" style="flex:1;" onclick="savePartyForm('${role}'${isVictim && existing ? `,'${existing.id}'` : ''})">Save</button>` +
    '</div>';

  showModal(html);
}

function val(id: string): string {
  const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
  return el?.value.trim() || '';
}
function checked(id: string): boolean {
  return (document.getElementById(id) as HTMLInputElement | null)?.checked || false;
}

/** id is only meaningful for role 'victim' (which record to update). */
export function savePartyForm(role: Role, id?: string): void {
  const now = new Date().toISOString();
  const st = store.getState();
  const common = {
    label: val('pf-label'),
    raceEthnicity: val('pf-raceEthnicity'),
    gender: val('pf-gender'),
    religion: val('pf-religion'),
    appearanceNotes: val('pf-appearance'),
    socioeconomicStatus: val('pf-ses'),
    notes: val('pf-notes')
  };

  if (role === 'victim') {
    const list = st.victims.slice();
    const prevCreated = id ? list.find((x) => x.id === id)?.createdAt : undefined;
    const rec: VictimRecord = {
      id: id || uid(),
      ...common,
      ageClass: val('pf-ageClass') as VictimAgeClass,
      type: val('pf-type') as VictimType,
      isLE: checked('pf-isLE'),
      relationshipToDefendant: val('pf-relationship') as VictimRelationship,
      conductContested: checked('pf-conductContested'),
      createdAt: prevCreated || now,
      updatedAt: now
    };
    const idx = list.findIndex((x) => x.id === rec.id);
    if (idx >= 0) list[idx] = rec;
    else list.push(rec);
    st.victims = list;
  } else {
    // Single-slot: replace whatever defendant exists (or create the only one).
    const prev = st.defendant;
    st.defendant = {
      id: prev?.id || uid(),
      ...common,
      status: val('pf-status') as DefendantStatus,
      createdAt: prev?.createdAt || now,
      updatedAt: now
    };
  }
  store.save();
  closeModal();
  showToast(`${role === 'victim' ? 'Victim' : 'Defendant'} saved`);
  activeTab = role;
  renderPartiesPage();
}

/** id is only meaningful for role 'victim' (which record to remove). */
export async function deleteParty(role: Role, id?: string): Promise<void> {
  const ok = await confirmApp(
    `Delete ${role === 'victim' ? 'Victim' : 'Defendant'}`,
    'Remove this record? This cannot be undone.',
    'Delete',
    'btn-danger'
  );
  if (!ok) return;
  const st = store.getState();
  if (role === 'victim') {
    if (!id) return;
    st.victims = st.victims.filter((x) => x.id !== id);
  } else {
    st.defendant = null;
  }
  store.save();
  showToast('Deleted');
  renderPartiesPage();
}
